import { useMemo } from 'react';

import { getPath } from '~/src/common/components/Navigation/utils';
import ProductsGrid from '~/src/common/components/ProductsGrid';
import { useNavigation } from '~/src/queries/api-ecom/generated/api-ecom';
import { type CatalogItem } from '~/src/typings/products/types';

interface Props {
  category: {
    id: string;
    slug: string;
    items: CatalogItem[];
  };
}

const Products = ({ category }: Props) => {
  const { slug, items } = category;
  const productCount = items.length;

  // Analytics: Path en cours
  const { data: navigation } = useNavigation();
  const path = useMemo(() => {
    if (!navigation) return null;
    return getPath(navigation, slug);
  }, [navigation, slug]);

  // Analytics: Utilisé par amplitude pour l'ATC
  const categoryAnalyticsProperties = useMemo(() => {
    if (!path || !navigation) return undefined;
    return {
      'number of products': productCount,
      'number of families': navigation.families.length,
      ...('family' in path && {
        'family name': path.family.item.name,
        'family position': path.family.index + 1,
        'number of categories': path.family.item.categories.length,
      }),
      ...('category' in path && {
        'category name': path.category.item.name,
        'category position': path.category.index + 1,
        'number of subcategories': path.category.item.children.length,
      }),
      ...('subcategory' in path && {
        'subcategory name': path.subcategory.item.name,
        'subcategory position': path.subcategory.index + 1,
      }),
    };
  }, [path, navigation, productCount]);

  // Analytics: Envoyé au backend à l'ATC
  const atcPathProperties = useMemo(() => {
    const maincat =
      path && 'category' in path
        ? path.category.item
        : path && 'recipecategory' in path
        ? path.recipecategory.item
        : undefined;
    const subcat =
      path && 'subcategory' in path
        ? path.subcategory.item
        : path && 'recipesubcategory' in path
        ? path.recipesubcategory.item
        : undefined;
    return {
      ...(maincat && { categoryName: maincat.name }),
      ...(subcat && { subcategory: { id: subcat.id, name: subcat.name } }),
    };
  }, [path]);

  const source = useMemo(
    () => ({
      type: 'products list' as const,
      detail: {
        categoryId: category.id,
      },
    }),
    [category.id],
  );

  return (
    <ProductsGrid
      products={items}
      eventSource="products list"
      source={source}
      subcategory={atcPathProperties.subcategory}
      categoryName={atcPathProperties.categoryName}
      categoryAnalyticsProperties={categoryAnalyticsProperties}
    />
  );
};

export default Products;
